import { useMutation, useQueryClient } from 'react-query'
import { message } from 'antd'
import { postJSON } from 'common/services/api'
import { Referral } from '../types'

interface UseReferralArchiveReturn {
  archiveReferral: (referralId: string) => Promise<Referral>
  unarchiveReferral: (referralId: string) => Promise<Referral>
  isArchiving: boolean
}

export const useReferralArchive = (
  residentId: string,
  onSuccess?: () => void
): UseReferralArchiveReturn => {
  const queryClient = useQueryClient()

  const archiveMutation = useMutation(
    async ({
      referralId,
      archive
    }: {
      referralId: string
      archive: boolean
    }) => {
      const response = await postJSON({
        url: `/api/list/referrals/${referralId}/archive`,
        method: 'PATCH',
        json: { archive }
      })

      return response.result as Referral
    },
    {
      onSuccess: (_, variables) => {
        message.success(
          variables.archive
            ? 'Referral archived successfully'
            : 'Referral restored successfully'
        )
        // Invalidate referrals list to refetch
        queryClient.invalidateQueries(['referrals', residentId])
        if (onSuccess) {
          onSuccess()
        }
      },
      onError: (error: any) => {
        message.error(error?.message || 'Failed to update referral')
      }
    }
  )

  const archiveReferral = async (referralId: string): Promise<Referral> => {
    return archiveMutation.mutateAsync({ referralId, archive: true })
  }

  const unarchiveReferral = async (
    referralId: string
  ): Promise<Referral> => {
    return archiveMutation.mutateAsync({ referralId, archive: false })
  }

  return {
    archiveReferral,
    unarchiveReferral,
    isArchiving: archiveMutation.isLoading
  }
}
